import { axInst } from "../../../Components/Login/Service";
import WaterMark from "../../../Components/WaterMark/WaterMark";
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { IoMdArrowDropdown } from "react-icons/io";
import { TbHandClick } from "react-icons/tb";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Loader } from "rsuite";
import { useSelector } from "react-redux";

const region_table_details = async (body) => {
  try {
    const res = await axInst.post("retails/region-wise-retails", body);
    const obj = res.data;
    const sum = obj.reduce(
      (acc, e) => ({
        today_sum: acc.today_sum + e.Today,
        total_sum: acc.total_sum + e.Total,
        lmsd_sum: acc.lmsd_sum + e.LMSD,
      }),
      { today_sum: 0, total_sum: 0, lmsd_sum: 0 },
    );

    return {
      obj,
      today_sum: sum.today_sum,
      total_sum: sum.total_sum,
      lmsd_sum: sum.lmsd_sum,
    };
  } catch (error) {
    console.error(error);
    return {
      obj: [],
      today_sum: 0,
      total_sum: 0,
      lmsd_sum: 0,
    };
  }
};

const state_table_details = async (requestBody) => {
  try {
    const res = await axInst.post("retails/state-wise-retails", requestBody);
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

const RegionWise = () => {
  const { data: filterdata, submitted } = useSelector((state) => state.filters);
  const { data, isFetching, isError } = useQuery({
    queryKey: ["regionTable", submitted],
    queryFn: () => region_table_details(filterdata),
    initialData: {
      obj: [],
      today_sum: 0,
      total_sum: 0,
    },
    refetchInterval: 300000,
    refetchIntervalInBackground: true,
    cacheTime: 0,
    staleTime: 0,
  });

  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    return () => {
      setExpandedRows({});
    };
  }, []);

  const toggleRowExpanded = async (region, filterdata) => {
    try {
      if (!expandedRows[region]) {
        const requestBody = {
          ...filterdata,
          region: [region], // override/add region
        };
        const stateData = await state_table_details(requestBody);
        setExpandedRows((prev) => ({
          ...prev,
          [region]: stateData,
        }));
      } else {
        setExpandedRows((prev) => ({
          ...prev,
          [region]: !prev[region] ? expandedRows[region] : false,
        }));
      }
    } catch (error) {
      console.error("Error toggling row expansion:", error);
    }
  };

  const columns = [
    {
      id: "region",
      header: "Region",
      accessorFn: (value) => value.Region,
      cell: (info) => (
        <span className="text-left" title="Click to view state wise retail">
          {info?.getValue()}
        </span>
      ),
      className: "text-left",
    },
    {
      header: "Today's Retail",
      accessorFn: (value) => value.Today,
      cell: ({ row }) => (
        <span className="text-right">{row?.original.Today}</span>
      ),
      className: "text-right",
    },

    {
      header: "LMSD",
      title: "Last Month's Same Day Retail",
      accessorFn: (value) => value.LMSD,
      cell: ({ row }) => (
        <span className="text-right" title="Last Month's Same Day Retail ">
          {row?.original.LMSD}
        </span>
      ),
      className: "text-right",
    },
    {
      header: "Total",
      accessorFn: (value) => value.Total,
      cell: ({ row }) => (
        <span className="text-right">{row?.original.Total}</span>
      ),
      className: "text-right",
    },
  ];

  const table = useReactTable({
    data: data?.obj ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: sorting,
      globalFilter: filtering,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
  });

  return (
    <div className=" relative ">
      <header className="pb-2 md:text-base text-xs font-semibold text-[#4c4c4c]">
        Region and State wise Retail
      </header>
      <div className="self-center overflow-auto h-[13.6rem]">
        {isError && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Error Occurred</p>
          </div>
        )}
        {isFetching && (
          <div className="grid items-center justify-center w-full h-full">
            <Loader />
          </div>
        )}
        {!isFetching && !isError && !data && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Data Not Available</p>
          </div>
        )}
        {!isFetching && !isError && data && (
          <>
            <WaterMark />
            <table className="w-full">
              <thead className="sticky text-white top-0 bg-[#2A5775] text-left z-[1]">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        title={header.column.columnDef.title}
                        className="p-1 text-sm lg:text-base border text-center border-gray-300"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`even:bg-gray-200 odd:bg-white ${
                        rowIndex === 0 ? "cursor-default" : "cursor-pointer"
                      }`}
                      onClick={() =>
                        row.original.Region !== "All Regions" &&
                        toggleRowExpanded(row.original.Region, filterdata)
                      }
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <td
                          key={cell.id}
                          className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8 relative"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                          {cellIndex === 0 &&
                            row.original.Region !== "All Regions" && (
                              <IoMdArrowDropdown className="h-3 w-3 text-gray-400 ml-1 inline-block" />
                            )}
                        </td>
                      ))}
                    </tr>
                    {expandedRows[row.original.Region] &&
                      expandedRows[row.original.Region] !== false &&
                      expandedRows[row.original.Region].map(
                        (stateData, index) => (
                          <tr key={index} className="bg-[#2A5775] text-white">
                            <td className="p-1 text-center text-xs lg:text-sm border border-gray-300 h-8">
                              {stateData.State}
                            </td>
                            <td className="p-1 text-center text-xs lg:text-sm border border-gray-300 h-8">
                              {stateData.Today}
                            </td>
                            <td className="p-1 text-center text-xs lg:text-sm border border-gray-300 h-8">
                              {stateData.LMSD}
                            </td>
                            <td className="p-1 text-center text-xs lg:text-sm border border-gray-300 h-8">
                              {stateData.Total}
                            </td>
                          </tr>
                        ),
                      )}
                  </React.Fragment>
                ))}
              </tbody>

              <tfoot className="sticky bottom-0 bg-[#2A5775] text-white">
                <tr>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    Grand Total
                  </td>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    {data.today_sum}
                  </td>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    {data.lmsd_sum}
                  </td>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    {data.total_sum}
                  </td>
                </tr>
              </tfoot>
            </table>
            <p className="text-xs mt-2 flex items-center">
              <TbHandClick /> on Region to view States Retail
            </p>
          </>
        )}
      </div>
    </div>
  );
};
export default RegionWise;
